import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Shield,
  Users,
  Zap,
  ArrowRight,
  RefreshCw,
  Database,
  Lock,
  ChevronRight,
} from 'lucide-react';

export default function Documentation() {
  const { t } = useTranslation();

  const sections = [
    {
      id: 'overview',
      title: t('publicPages.docs.sections.overviewTitle'),
      icon: BookOpen,
      content: t('publicPages.docs.sections.overviewContent'),
    },
    {
      id: 'hubspot-integration',
      title: t('publicPages.docs.sections.hubspotTitle'),
      icon: Database,
      content: t('publicPages.docs.sections.hubspotContent'),
      details: [
        t('publicPages.docs.sections.hubspotDetail1'),
        t('publicPages.docs.sections.hubspotDetail2'),
        t('publicPages.docs.sections.hubspotDetail3'),
        t('publicPages.docs.sections.hubspotDetail4'),
      ],
    },
    {
      id: 'how-it-works',
      title: t('publicPages.docs.sections.howItWorksTitle'),
      icon: Zap,
      steps: [
        {
          title: t('publicPages.docs.sections.step1Title'),
          description: t('publicPages.docs.sections.step1Desc'),
        },
        {
          title: t('publicPages.docs.sections.step2Title'),
          description: t('publicPages.docs.sections.step2Desc'),
        },
        {
          title: t('publicPages.docs.sections.step3Title'),
          description: t('publicPages.docs.sections.step3Desc'),
        },
        {
          title: t('publicPages.docs.sections.step4Title'),
          description: t('publicPages.docs.sections.step4Desc'),
        },
      ],
    },
    {
      id: 'permissions',
      title: t('publicPages.docs.sections.permissionsTitle'),
      icon: Shield,
      content: t('publicPages.docs.sections.permissionsContent'),
      scopes: [
        {
          name: 'crm.objects.contacts.read',
          description: t('publicPages.docs.sections.scope1Desc'),
        },
        {
          name: 'crm.schemas.contacts.read',
          description: t('publicPages.docs.sections.scope2Desc'),
        },
      ],
    },
    {
      id: 'data-security',
      title: t('publicPages.docs.sections.securityTitle'),
      icon: Lock,
      content: t('publicPages.docs.sections.securityContent'),
      details: [
        t('publicPages.docs.sections.securityDetail1'),
        t('publicPages.docs.sections.securityDetail2'),
        t('publicPages.docs.sections.securityDetail3'),
        t('publicPages.docs.sections.securityDetail4'),
        t('publicPages.docs.sections.securityDetail5'),
      ],
    },
    {
      id: 'disconnecting',
      title: t('publicPages.docs.sections.disconnectingTitle'),
      icon: RefreshCw,
      content: t('publicPages.docs.sections.disconnectingContent'),
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img src="/logo-color.png" alt="Clareefai" className="h-8 w-auto" />
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/support" className="text-gray-600 hover:text-gray-900 transition-colors">
              {t('publicPages.docs.nav.support')}
            </Link>
            <Link
              to="/login"
              className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              {t('publicPages.docs.nav.signIn')}
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="flex items-center gap-2 text-sm text-blue-600 font-medium mb-4">
            <BookOpen className="w-4 h-4" />
            {t('publicPages.docs.eyebrow')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {t('publicPages.docs.hero.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            {t('publicPages.docs.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Table of Contents */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <nav className="bg-gray-50 rounded-xl p-5 mb-10">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            {t('publicPages.docs.onThisPage')}
          </h2>
          <ul className="space-y-1.5">
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 transition-colors py-1"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Sections */}
        <div className="space-y-14">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <section key={section.id} id={section.id} className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                </div>

                {'content' in section && (
                  <p className="text-gray-600 leading-relaxed mb-4">{section.content}</p>
                )}

                {'details' in section && section.details && (
                  <ul className="space-y-2.5 mt-4">
                    {section.details.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <ArrowRight className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {'steps' in section && section.steps && (
                  <ol className="mt-6 space-y-6">
                    {section.steps.map((step, i) => (
                      <li key={i} className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
                          {i + 1}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">{step.title}</h3>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}

                {'scopes' in section && section.scopes && (
                  <div className="mt-5 bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                    <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        {t('publicPages.docs.sections.scopesHeader')}
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {section.scopes.map((scope) => (
                        <div key={scope.name} className="px-4 py-3">
                          <code className="text-sm font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                            {scope.name}
                          </code>
                          <p className="text-sm text-gray-600 mt-1">{scope.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <p>© {new Date().getFullYear()} Clareefai. {t('publicPages.docs.footer.rights')}</p>
            <div className="flex items-center gap-6">
              <Link to="/docs" className="hover:text-white transition-colors">
                {t('publicPages.docs.footer.documentation')}
              </Link>
              <Link to="/support" className="hover:text-white transition-colors">
                {t('publicPages.docs.footer.support')}
              </Link>
              <a
                href="https://clareefai.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                clareefai.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
